/** UI state owned by the shell: breadcrumbs, collapsed nav, AI assistant drawer, command palette. */
import { useEffect } from 'react';
import { create } from 'zustand';

export interface Crumb {
  label: string;
  to?: string;
}

interface ShellState {
  crumbs: Crumb[];
  navOpen: boolean;
  aiOpen: boolean;
  /** System the AI assistant is scoped to when opened from a page (null = header scope / first system). */
  aiSystemId: string | null;
  paletteOpen: boolean;
  setCrumbs: (crumbs: Crumb[]) => void;
  toggleNav: () => void;
  openAi: (systemId?: string | null) => void;
  closeAi: () => void;
  setPaletteOpen: (open: boolean) => void;
}

const NAV_KEY = 'uatp.admin.nav';

function readNav(): boolean {
  try {
    return localStorage.getItem(NAV_KEY) !== 'collapsed';
  } catch {
    return true;
  }
}

export const useShell = create<ShellState>((set) => ({
  crumbs: [],
  navOpen: readNav(),
  aiOpen: false,
  aiSystemId: null,
  paletteOpen: false,
  setCrumbs: (crumbs) => set({ crumbs }),
  toggleNav: () =>
    set((s) => {
      try {
        localStorage.setItem(NAV_KEY, s.navOpen ? 'collapsed' : 'open');
      } catch {
        /* ignore */
      }
      return { navOpen: !s.navOpen };
    }),
  openAi: (systemId = null) => set({ aiOpen: true, aiSystemId: systemId }),
  closeAi: () => set({ aiOpen: false }),
  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
}));

/**
 * Declares the page's breadcrumb trail and document title. The last crumb is the current page.
 * `usePageMeta([{ label: 'Systems', to: '/systems' }, { label: system.name }])`
 */
export function usePageMeta(crumbs: Crumb[], title?: string): void {
  const setCrumbs = useShell((s) => s.setCrumbs);
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    setCrumbs(crumbs);
    const last = title ?? crumbs[crumbs.length - 1]?.label;
    document.title = last ? `${last} · UAT Proofing` : 'UAT Proofing';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, title, setCrumbs]);
}
